import { ADMIN_VIEWS_READ, type GatedNavItem, mayOpenScreen } from './permissions.js';

/**
 * THE SIX HUBS (owner master UX/IA directive + Phase A navigation ruling —
 * LOCKED):
 *
 *   Today · Operations · People · Compliance · Messages · Intelligence
 *
 * plus the quiet admin foot (Integrations · Settings · Your profile). The
 * ruled mental model:
 *
 *   SIDEBAR              = HUB
 *   TOP NAVIGATION CARDS = MAJOR DESTINATIONS INSIDE THE HUB
 *   PAGE BELOW           = ACTUAL WORK SURFACE
 *
 * This file is the single statement of what lives inside each hub.
 * `navigation.ts` builds the rail from it and `shell/HubCards.tsx` renders it
 * as the in-hub card row, so the rail and the hub's own face cannot disagree
 * about the hub's contents.
 *
 * CARDS ARE GROUPED DESTINATIONS, NOT ONE-PER-OLD-ROUTE (the ruling's own
 * words: "do not make every old route its own navigation card… approximately
 * 4–6 primary cards per Hub"). Deep pages stay reachable contextually from
 * their destination (timesheets from Time clock, a session from the workforce
 * board) and through global Find.
 *
 * THE LAW THIS SERVES (directive: "fewer places to start — everything still
 * reachable"): the rail items that leave the primary rail keep their
 * capabilities. Every demoted destination is here with the SAME permission
 * gate its rail entry carried — each gate was argued from the server's own
 * guard (see gate-keys.test.ts), and consolidation is not an occasion to
 * re-litigate them.
 *
 * WHAT IS DELIBERATELY ABSENT, AND WHY (the do-not-fake rule) — three
 * different kinds of absent, kept distinct on the founder's instruction:
 *
 *   Insights (Intelligence) — NO BACKEND CAPABILITY yet: nothing persists a
 *     finding to surface (engineering request INT1).
 *   EVV (Compliance) — CAPABILITY EXISTS INSIDE ANOTHER SURFACE: visit/
 *     check-in verification lives in the Time clock's verification panel;
 *     there is no standalone EVV route to point a card at without dressing
 *     another screen in a new name.
 *
 * A card that 404s or that renames another screen would be fiction; each of
 * these appears here the day its real surface exists.
 *
 * Cards are in LANDING-PREFERENCE order: when a principal may not open a
 * hub's face, the rail points their hub entry at the first card they hold
 * (`filterNavGroups`), so consolidation never manufactures a 403.
 *
 * ROUTES ARE UNTOUCHED. Every href below is a route App.tsx already
 * registers, with its route-level gate in place; deep links and old bookmarks
 * resolve exactly as before.
 */

export type HubKey = 'today' | 'operations' | 'people' | 'compliance' | 'messages' | 'intelligence';

/** A compact in-hub navigation card: a destination plus one line of orientation. */
export interface HubCard extends GatedNavItem {
  /** One quiet line under the label — what lives behind this destination. */
  descriptor: string;
}

export interface Hub {
  key: HubKey;
  label: string;
  icon: GatedNavItem['icon'];
  /** The hub's face — where its rail entry lands when openable. */
  face: { href: string; permission?: string };
  /**
   * The card row shown at the top of the hub — approximately 4–6 primary
   * destinations. Empty for Messages, which opens straight into the
   * conversation inbox.
   */
  cards: HubCard[];
  /**
   * Secondary destinations (founder Phase A review §3): still discoverable —
   * rendered as quiet links after the card row, in the rail's landing
   * fallback, and in the destination filter — but deliberately not
   * equal-weight cards.
   */
  more?: HubCard[];
}

export const HUBS: readonly Hub[] = [
  {
    key: 'today',
    label: 'Today',
    icon: 'today',
    face: { href: '/' },
    /**
     * THREE DIFFERENT JOBS, one card each (founder Phase A review §1):
     * Right Now is the live command center, Dashboard the summarized
     * organizational operating picture, Analytics the investigative
     * workspace. The route names are legacy and deliberately preserved —
     * `/dashboards` (the configurable, curated-metric summary board) is the
     * user-facing DASHBOARD; `/dashboard` (the multi-view analytical
     * workspace: trends, comparisons, filters, drill-down) is the user-facing
     * ANALYTICS. One of each; the old Executive dashboard / Dashboards pair
     * of names does not come back. `dashboard:read` on both is widened by
     * `keysThatOpen` to the analytics surface's real guard list, so the
     * analyst role keeps its reach.
     */
    cards: [
      {
        key: 'right-now',
        label: 'Right Now',
        descriptor: 'Live operational command center',
        icon: 'today',
        href: '/',
      },
      {
        key: 'dashboard',
        label: 'Dashboard',
        descriptor: 'The organization at a glance',
        icon: 'chart',
        href: '/dashboards',
        permission: 'dashboard:read',
      },
      {
        key: 'analytics',
        label: 'Analytics',
        descriptor: 'Trends · comparisons · drill-down',
        icon: 'pulse',
        href: '/dashboard',
        permission: 'dashboard:read',
      },
      {
        key: 'attention',
        label: 'Needs attention',
        descriptor: 'What needs a human now',
        icon: 'look',
        href: '/attention',
      },
      {
        key: 'notifications',
        label: 'Notifications',
        descriptor: 'Everything sent to you',
        icon: 'bell',
        href: '/notifications',
        permission: 'notification:read',
      },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    icon: 'planned',
    // The scheduler IS the hub's canvas (directive: scheduler-as-Operations-
    // canvas): the face is the schedule; the cards are the ruled six.
    face: { href: '/scheduling', permission: 'schedule:read' },
    cards: [
      {
        key: 'schedule',
        label: 'Schedule',
        descriptor: 'Calendar · upcoming shifts · coverage',
        icon: 'planned',
        href: '/scheduling',
        permission: 'schedule:read',
      },
      {
        // The escalation had no reader. `escalateUnanswered` has written
        // `escalated_to_user_id` since the domain shipped and nothing could
        // query it back, so a manager was named as accountable for a question
        // they had no way to see.
        key: 'cancellation-asks',
        label: 'Cancellation asks',
        descriptor: 'Who was asked why · who has not answered · what escalated',
        icon: 'planned',
        href: '/scheduling/cancellations',
        permission: 'schedule:manage',
      },
      {
        key: 'time',
        label: 'Time & Timesheets',
        descriptor: 'Actual time · payroll review',
        icon: 'clock',
        href: '/time',
        permission: ADMIN_VIEWS_READ,
      },
      {
        key: 'tasks',
        label: 'Tasks',
        descriptor: 'Operational work queue',
        icon: 'decisions',
        href: '/tasks',
        permission: 'task:read',
      },
      {
        key: 'mileage',
        label: 'Mileage',
        descriptor: 'Trips · reconciliation · review',
        icon: 'trail',
        href: '/mileage',
        permission: 'mileage:review',
      },
      {
        key: 'visits',
        label: 'Visits',
        descriptor: 'Planned and delivered visit records',
        icon: 'delivery',
        href: '/visits',
        permission: 'service:read',
      },
      {
        // Lands on Approvals until the Phase E Decision Queue unifies the
        // five review modules behind one shape; the card already wears the
        // destination's ruled name so the vocabulary arrives first.
        key: 'decisions',
        label: 'Decisions',
        descriptor: 'Approvals · exceptions · corrections',
        icon: 'decisions',
        href: '/time/approvals',
        permission: 'timesheet:approve',
      },
    ],
    /**
     * SECONDARY, NOT EQUAL-WEIGHT (founder Phase A review §3): Location
     * history keeps the discoverability its own ruling won — ungated, so an
     * administrator who has not been granted it still learns it exists
     * (location-discoverability.test.ts) — but it does not earn a primary
     * card just because the route exists. It renders as a quiet link after
     * the card row, stays deep-linked from the workforce board and worker
     * profiles, and the screen and server still refuse coordinates without
     * the grant.
     */
    more: [
      {
        key: 'live-map',
        label: 'Location history',
        descriptor: 'Recorded positions for a session',
        icon: 'mapPin',
        href: '/field/map',
      },
    ],
  },
  {
    key: 'people',
    label: 'People',
    icon: 'people',
    face: { href: '/people' },
    // Deliberately shallow — the ruling's own fence: workforce operations, not
    // an HRIS/EHR-style directory. Supervision and credentials are Compliance
    // questions; a person's own record carries their reporting line.
    cards: [
      {
        key: 'team',
        label: 'Team',
        descriptor: 'Roster · profiles · access',
        icon: 'people',
        href: '/people',
      },
      {
        key: 'org-chart',
        label: 'Org chart',
        descriptor: 'Reporting lines, delegation and coverage',
        icon: 'people',
        href: '/org-chart',
        permission: 'orgchart:read',
      },
    ],
  },
  {
    key: 'compliance',
    label: 'Compliance',
    icon: 'trust',
    face: { href: '/compliance', permission: 'audit:read' },
    // The ruled row minus EVV (see the header: no surface to point at yet).
    // Overview is the face itself, so the card row begins with it and the
    // landing fallback order starts at Documentation — the widest-held key of
    // the four, so a principal without `audit:read` lands on the section most
    // likely to be theirs.
    cards: [
      {
        key: 'overview',
        label: 'Overview',
        descriptor: 'Compliance posture at a glance',
        icon: 'trust',
        href: '/compliance',
        permission: 'audit:read',
      },
      {
        key: 'documentation',
        label: 'Documentation',
        descriptor: 'Service notes · open documentation',
        icon: 'documented',
        href: '/documentation',
        permission: 'documentation:read',
      },
      {
        // A SECOND documentation card, on a DIFFERENT key, and the two are not
        // interchangeable. `documentation:read` opens the records surface,
        // whose detail view carries the visit note; `documentation:read_metadata`
        // opens this one, which is counts and states and can carry no note at
        // all. Ruling 7 binds Intelligence and Agents to the second and not the
        // first, and until this card existed the second had no human door: the
        // model could ask how much documentation was outstanding and the
        // supervisor accountable for it could not.
        key: 'documentation-completeness',
        label: 'Documentation completeness',
        descriptor: 'How much is outstanding · how far past due',
        icon: 'documented',
        href: '/documentation/completeness',
        permission: 'documentation:read_metadata',
      },
      {
        key: 'supervision',
        label: 'Supervision',
        descriptor: 'Required supervision · sessions held',
        icon: 'handbook',
        href: '/workforce/supervision',
        permission: 'supervision:read',
      },
      {
        key: 'credentials',
        label: 'Credentials',
        descriptor: 'Licences · expirations · verification',
        icon: 'trust',
        href: '/workforce/credentials',
        permission: 'credential:read',
      },
      {
        key: 'incidents',
        label: 'Incidents',
        descriptor: 'What happened · who was told',
        icon: 'look',
        href: '/compliance/incidents',
        permission: 'incidents:read',
      },
      {
        key: 'audit-log',
        label: 'Audit',
        descriptor: 'Who did what, when',
        icon: 'documented',
        href: '/compliance/audit',
        permission: 'audit:read',
      },
    ],
    // BELOW THE FOLD, AND ELIGIBILITY IS HERE FOR A REASON.
    //
    // `directory:read` is seeded to the `worker` role, so an Eligibility card
    // gated on it would appear in the admin rail for field workers. The server
    // narrows the list and withholds reasons, so that is safe rather than a
    // leak -- but a card promising "who is held back from service work" to
    // somebody who will only ever see themselves is a worse answer than not
    // offering it. It moves up to `cards` when the domain's own `eligibility:*`
    // keys are seeded; the borrowed key is the reason, not the placement.
    more: [
      {
        key: 'eligibility',
        label: 'Eligibility',
        descriptor: 'Who is held back from service work',
        icon: 'trust',
        href: '/workforce/eligibility',
        permission: 'directory:read',
      },
      {
        key: 'forms',
        label: 'Forms',
        descriptor: 'Instruments · approvals · publication',
        icon: 'documented',
        href: '/compliance/forms',
        permission: 'knowledge:read',
      },
    ],
  },
  {
    key: 'messages',
    label: 'Messages',
    icon: 'messages',
    face: { href: '/messages' },
    // Straight into the conversation inbox (ruling) — no sub-navigation cards.
    cards: [],
  },
  {
    key: 'intelligence',
    label: 'Intelligence',
    // The hub wears the hub's name (directive); the room inside keeps the
    // product name Ask Phibian, and the ⌘I panel and global Find still answer
    // to it.
    icon: 'intelligence',
    face: { href: '/intelligence', permission: 'intelligence:use' },
    // Ask Phibian · Insights · Automations · Library. INT1 is built — findings
    // are persisted, reachable and seeded — so Insights is a real destination
    // rather than a reserved name.
    //
    // Agents is `agents:read` because it opens the list. Since 2026-10-07 that
    // list also carries the readiness panel (every prerequisite: Phibian
    // enablement, the organization's consent, the reader's agent keys) and the
    // approvals inbox, and its "Set up an agent" button opens /agents/deploy
    // for `agents:configure` holders — which, by owner directive of that day,
    // includes org_admin (migration 2934000000000).
    cards: [
      {
        key: 'ask',
        label: 'Ask Phibian',
        descriptor: 'Questions over your own records',
        icon: 'intelligence',
        href: '/intelligence',
        permission: 'intelligence:use',
      },
      {
        key: 'automations',
        label: 'Automations',
        descriptor: 'What runs on its own · what it did',
        icon: 'automations',
        // The Intelligence surface, not Settings: reading what automations
        // did belongs to this hub; changing what they may do stays in
        // Settings -> Rules, reached from the page's manage link.
        href: '/intelligence/automations',
        permission: 'rules:read',
      },
      {
        key: 'insights',
        label: 'Insights',
        descriptor: 'What Phibian noticed · evidence · what to do',
        icon: 'intelligence',
        href: '/insights',
        permission: 'insights:read',
      },
      {
        // Founder ruling 2 of 2026-09-08. `recommendation:read` is seeded to
        // org_admin, supervisor and analyst and had no destination at all —
        // three live routes, no client, no card. This is the click path, and
        // it is what makes the grant a capability rather than a tick.
        key: 'recommendations',
        label: 'Recommendations',
        descriptor: 'What Phibian advised · evidence · what happened',
        icon: 'intelligence',
        href: '/intelligence/recommendations',
        permission: 'recommendation:read',
      },
      {
        key: 'agents',
        label: 'Agents',
        descriptor: 'Set up · approve · what is running · what it did',
        icon: 'automations',
        href: '/agents',
        permission: 'agents:read',
      },
      {
        key: 'virtual-workforce',
        label: 'Talk to Phoebe',
        descriptor: 'Virtual Workforce · the intake coordinator and the work underneath',
        icon: 'automations',
        href: '/virtual-workforce',
        permission: 'agents:read',
      },
      {
        key: 'library',
        label: 'Library',
        descriptor: 'What Phibian knows about you · sources · approvals',
        icon: 'intelligence',
        href: '/library',
        permission: 'knowledge:read',
      },
    ],
    /**
     * SECONDARY, AND GATED ON A DIFFERENT KEY FROM THE AGENTS CARD ABOVE.
     *
     * `agents:read` opens the list of what is running; `agents:configure` is
     * the canonical Agent-GOVERNANCE authority (founder tenant-consent ruling,
     * 2026-09-08). Since the 2026-10-07 owner directive (migration
     * 2934000000000) org_admin holds it; anyone else holds it by a direct
     * grant. A reader can hold either without the other, and gating
     * this on the card's key would hide the consent decision from the one
     * person authorized to make it.
     *
     * It is `more` rather than a seventh card because the ruling's own framing
     * is that this is done rarely and deliberately: an organization decides
     * once whether operational agents may run on its data, and sets an agent up
     * when it wants one. The card row is for places people go; this is a place
     * people act.
     */
    more: [
      {
        key: 'agent-setup',
        label: 'Set up an agent',
        descriptor: 'Whether agents may run here · configure and deploy one',
        icon: 'automations',
        href: '/agents/deploy',
        permission: 'agents:configure',
      },
    ],
  },
];

/** The hub whose face, cards, or secondary links contain `path`. */
export function hubForPath(path: string): Hub | undefined {
  const owns = (href: string) => path === href || (href !== '/' && path.startsWith(`${href}/`));
  return HUBS.find(
    (h) =>
      owns(h.face.href) ||
      h.cards.some((c) => owns(c.href)) ||
      (h.more ?? []).some((c) => owns(c.href)),
  );
}

/**
 * The rail-entry resolution list for a hub: its face, then its cards, then
 * its secondary links, in landing-preference order (first openable wins —
 * see `GatedNavItem.enter`). The face is repeated as a card on some hubs;
 * `filterNavGroups` only reads the first openable entry, so the duplication
 * is harmless there.
 */
export function hubEnter(hub: Hub): Array<{ href: string; permission?: string }> {
  return [
    hub.face,
    ...[...hub.cards, ...(hub.more ?? [])].map((c) => ({
      href: c.href,
      permission: c.permission,
    })),
  ];
}

/**
 * Every destination a principal can CLICK to: hub faces and cards their
 * permissions open. This is the set the navigation tests assert reachability
 * against, now that "in the rail" and "reachable by clicking" are no longer
 * the same sentence.
 */
export function clickableDestinations(permissions: readonly string[]): string[] {
  const out: string[] = [];
  for (const hub of HUBS) {
    for (const d of hubEnter(hub)) {
      if (mayOpenScreen(permissions, d.permission)) out.push(d.href);
    }
  }
  return [...new Set(out)];
}
